import React, { useCallback, useEffect, useState } from 'react'
import CheckInDetailsTable from './component/CheckInDetailsTable'
import RandomCheckInSelector from './component/RandomCheckInSelector'
import { FetchAdminRandomCheckInPayload } from '@/services/types/APIRequestType'
import { FetchAdminRandomCheckInResponse } from '@/services/types/APIResponseType'
import useGetAdminRandomCheckInButtonStatus from '@/services/hooks/useGetAdminRandomCheckInButtonStatus'
import usePostAdminRandomCheckIn from '@/services/hooks/usePostAdminRandomCheckIn'
import { getDateRange } from '@/utils'
import { TimePeriod } from '@/utils/config/user-enums'
import Loader from '@/components/Loader'

export interface TableDataPayload {
  limit: number
  page: number
  payload: FetchAdminRandomCheckInPayload
}

const RandomCheckIns = () => {
  const itemsPerPage = 5

  const [activeFilter, setActiveFilter] = useState<TimePeriod>(TimePeriod.ALL)
  const [adminCheckInData, setAdminCheckInData] =
    useState<FetchAdminRandomCheckInResponse>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [verificationStatus, setVerificationStatus] = useState<string | null>(
    null,
  )

  const { data: checkInButtonStatus, isLoading } =
    useGetAdminRandomCheckInButtonStatus()
  const { mutate: getAdminRandomCheckIn, isPending } =
    usePostAdminRandomCheckIn()

  const handleFilterChange = (filter: TimePeriod) => {
    setActiveFilter(filter)
    setCurrentPage(1)
  }

  const handleVerificationStatusChange = (newStatus: string | null) => {
    setVerificationStatus(newStatus)
    setCurrentPage(1)
  }

  const getAdminRandomCheckInTableData = useCallback(
    (requestData: TableDataPayload) => {
      getAdminRandomCheckIn(requestData, {
        onSuccess: (response) => {
          if (response) {
            setAdminCheckInData(response.data)
          }
        },
      })
    },
    [getAdminRandomCheckIn],
  )

  useEffect(() => {
    const requestData: TableDataPayload = {
      limit: itemsPerPage,
      page: currentPage,
      payload: {
        ...getDateRange(activeFilter),
        ...(verificationStatus !== null && {
          authentication: verificationStatus,
        }),
      },
    }

    getAdminRandomCheckInTableData(requestData)
  }, [
    activeFilter,
    getAdminRandomCheckInTableData,
    itemsPerPage,
    currentPage,
    verificationStatus,
  ])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="flex flex-col gap-8 mt-9">
      <RandomCheckInSelector
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        verificationStatus={verificationStatus}
        setVerificationStatus={handleVerificationStatusChange}
        checkInButtonStatus={checkInButtonStatus?.data ?? { impose: null }}
      />
      <CheckInDetailsTable
        isLoading={isPending}
        adminCheckInTableData={adminCheckInData?.results || []}
        currentPage={currentPage}
        totalCount={adminCheckInData?.totalCount || 0}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  )
}

export default RandomCheckIns
